import { ChangeEvent, FormEvent, useEffect, useState } from 'react';

export interface QuoteValues {
  name: string;
  company: string;
  phone: string;
  email: string;
  product: string;
  quantity: string;
  location: string;
  message: string;
}

export type QuoteErrors = Partial<Record<keyof QuoteValues, string>>;
export type QuoteStatus = 'idle' | 'submitting' | 'success';

const emptyValues: QuoteValues = {
  name: '',
  company: '',
  phone: '',
  email: '',
  product: '',
  quantity: '',
  location: '',
  message: '',
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useQuoteForm(initialProduct = '') {
  const [values, setValues] = useState<QuoteValues>({ ...emptyValues, product: initialProduct });
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [errors, setErrors] = useState<QuoteErrors>({});
  const [status, setStatus] = useState<QuoteStatus>('idle');

  useEffect(() => {
    if (initialProduct) setValues((current) => ({ ...current, product: initialProduct }));
  }, [initialProduct]);

  const setField =
    (key: keyof QuoteValues) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      setValues((current) => ({ ...current, [key]: value }));
      setErrors((current) => (current[key] ? { ...current, [key]: undefined } : current));
    };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
  };

  const clearFile = () => {
    setFile(null);
    setFileKey((key) => key + 1);
  };

  const validate = (): QuoteErrors => {
    const next: QuoteErrors = {};
    if (!values.name.trim()) next.name = 'Please enter your name.';
    if (!values.phone.trim()) next.phone = 'Please enter a phone number.';
    if (!values.email.trim()) next.email = 'Please enter your email.';
    else if (!emailPattern.test(values.email.trim())) next.email = 'Please enter a valid email address.';
    if (!values.product) next.product = 'Please choose a product.';
    return next;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 1200);
  };

  const reset = () => {
    setValues({ ...emptyValues, product: initialProduct });
    clearFile();
    setErrors({});
    setStatus('idle');
  };

  return { values, file, fileKey, errors, status, setField, handleFile, clearFile, handleSubmit, reset };
}
